import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { restaurantsAPI, adminAPI } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import { Pencil, Trash2, ExternalLink } from 'lucide-react';

export default function RestaurantsManagement() {
  const { logout } = useAuth();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['admin-restaurants'],
    queryFn: () => restaurantsAPI.getAll().then((res) => res.data),
  });

  const deleteMutation = useMutation({
    mutationFn: adminAPI.deleteRestaurant,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-restaurants'] });
      alert('Restaurant deleted successfully');
    },
  });

  const handleDelete = (id: number, name: string) => {
    if (window.confirm(`Are you sure you want to delete "${name}"? This will also delete all associated offers.`)) {
      deleteMutation.mutate(id);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <Link to="/admin" className="text-gray-600 hover:text-gray-900">
                ← Back
              </Link>
              <h1 className="text-2xl font-bold text-gray-900">Manage Restaurants</h1>
            </div>
            <button
              onClick={logout}
              className="px-4 py-2 text-red-600 hover:text-red-700 font-medium"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <button className="px-6 py-2 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700">
              + Add New Restaurant
            </button>
          </div>

          {/* Restaurants Grid */}
          {isLoading ? (
            <div className="p-8 text-center text-gray-600">Loading...</div>
          ) : data?.data && data.data.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
              {data.data.map((restaurant: any) => (
                <div key={restaurant.id} className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition">
                  {restaurant.logoUrl && (
                    <img
                      src={restaurant.logoUrl}
                      alt={restaurant.name}
                      className="w-full h-32 object-cover bg-gray-100"
                    />
                  )}
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="font-semibold text-gray-900">{restaurant.name}</h3>
                        <p className="text-sm text-gray-600">{restaurant.area}</p>
                      </div>
                      {restaurant.isPremium && (
                        <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded">
                          Premium
                        </span>
                      )}
                    </div>
                    {restaurant.cuisineTypes && restaurant.cuisineTypes.length > 0 && (
                      <p className="text-sm text-gray-600 mb-3">
                        {restaurant.cuisineTypes.slice(0, 2).join(', ')}
                      </p>
                    )}
                    <div className="flex items-center gap-2 pt-3 border-t">
                      <Link
                        to={`/restaurants/${restaurant.id}`}
                        target="_blank"
                        className="flex-1 px-4 py-2 text-center bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition text-sm font-medium"
                      >
                        <ExternalLink className="w-4 h-4 inline mr-1" />
                        View
                      </Link>
                      <button className="px-3 py-2 text-blue-600 hover:bg-blue-50 rounded">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(restaurant.id, restaurant.name)}
                        className="px-3 py-2 text-red-600 hover:bg-red-50 rounded"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center text-gray-600">No restaurants found</div>
          )}
        </div>
      </div>
    </div>
  );
}
